import React, { useState, useEffect, Fragment } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { useLocalStorage } from "@mantine/hooks";
import superjson from "superjson";
import { useTranslation } from "react-i18next";
import { shuffleWithSeed } from "../../utils/Utils";
import { v4 as uuidv4 } from "uuid";

import useAxiosPublic from "../../hooks/useAxiosPublic";
import endpoints from "../../api/endpoints";
import Loader from "../../partials/Loader";
import PageNotFound from "../error/PageNotFound";
import PracticeHeader from "../../partials/practice/PracticeHeader";
import RedoQuestionListModal from "../../partials/practice/RedoQuestionListModal";
import QuestionMain from "../../partials/practice/QuestionMain";
import RedoQuestionAnswer from "../../partials/practice/RedoQuestionAnswer";

export default function PracticeWrongQuestion() {
  // i18n
  const { t } = useTranslation();

  const [quiz, setQuiz] = useState();
  const [question, setQuestion] = useState();
  const [next, setNext] = useState();
  const [prev, setPrev] = useState();

  const [isLoading, setLoading] = useState(true);
  const [userAnswers, setUserAnswers] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [submitted, setIsSubmitted] = useState(false);
  const [isSubmittionCorect, setIsSubmissionCorrect] = useState(false);
  const [startedAutoNext, setStartedAutoNext] = useState(false);

  const axiosPublic = useAxiosPublic();
  const controller = new AbortController();

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [autoNext, setAutoNext] = useLocalStorage({
    key: "autoNext",
    defaultValue: false,
  });

  // Modal
  const [questionListModalOpen, setQuestionListModalOpen] = useState(false);

  // Params
  const { quizId, setId } = useParams();
  const questionId = searchParams.get("questionId");

  // Record Practice Setting
  const [practiceSetting, setPracticeSetting] = useLocalStorage({
    key: `practiceSetting`,
    defaultValue: new Map(),
    serialize: superjson.stringify,
    deserialize: (map) =>
      map === undefined ? new Map() : superjson.parse(map),
    getInitialValueInEffect: false,
  });

  // Wrong History
  const [wrongStatistics, setWrongStatistics] = useLocalStorage({
    key: `wrongStatistics`,
    defaultValue: new Map(),
    serialize: superjson.stringify,
    deserialize: (map) =>
      map === undefined ? new Map() : superjson.parse(map),
    getInitialValueInEffect: false,
  });

  // Wrong History
  const [wrongHistory, setWrongHistory] = useLocalStorage({
    key: `wrongHistory`,
    defaultValue: new Set(),
    serialize: superjson.stringify,
    deserialize: (map) =>
      map === undefined ? new Set() : superjson.parse(map),
    getInitialValueInEffect: false,
  });

  // Get Previous Answer
  const [answerHistory, setAnswerHistory] = useLocalStorage({
    key: `practice-${quizId}-answer-history`,
    defaultValue: new Map(),
    serialize: superjson.stringify,
    deserialize: (map) =>
      map === undefined ? new Map() : superjson.parse(map),
    getInitialValueInEffect: false,
  });

  // Get Answer Statistics
  const [answerStatistics, setAnswerStatistics] = useLocalStorage({
    key: `practice-${quizId}-answer-statistics`,
    defaultValue: new Map(),
    serialize: superjson.stringify,
    deserialize: (map) =>
      map === undefined ? new Map() : superjson.parse(map),
    getInitialValueInEffect: false,
  });

  // Get Question List
  const getQuestionList = async () => {
    try {
      const res = await axiosPublic.get(
        `${endpoints.PRACTICE_URL}/${quizId}/${setId}`,
        {
          signal: controller.signal,
        }
      );

      const wrongQuestionIds = getWrongQuestionIds();

      // Filter Question by questionIds
      res.data.questions = res.data.questions.filter((question) => {
        return wrongQuestionIds.includes(question.id);
      });

      // Sort by wrong wrong question ids
      res.data.questions.sort((a, b) => {
        return wrongQuestionIds.indexOf(a.id) - wrongQuestionIds.indexOf(b.id);
      });

      setQuiz(res.data);

      if (answerStatistics.size === 0) {
        // Push initial statistics
        res.data.questions.map((question) => {
          answerStatistics.set(question.id, "untouched");
        });
        setAnswerStatistics(answerStatistics);
      }

      if (!questionId) {
        setSearchParams({ questionId: res.data.questions[0].id || undefined });
      }
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  // Get Answers
  const getAnswers = async (questionId) => {
    try {
      const res = await axiosPublic.get(
        `${endpoints.PRACTICE_URL}/${quizId}/${setId}/${questionId}`,

        {
          signal: controller.signal,
        }
      );

      const practiceObj = practiceSetting.get(quizId);

      // Shuffle Answers
      if (practiceObj.isAnswerShuffled === true) {
        res.data.answers = shuffleWithSeed(
          res.data.answers,
          practiceObj.practiceId + questionId
        );
      }

      setQuestion(res.data);

      // Set Next and Prev
      if (quiz) {
        const idx = quiz.questions.findIndex((q) => q.id === questionId);
        if (idx > 0) {
          setPrev(quiz.questions[idx - 1].id);
        } else {
          setPrev(null);
        }

        if (idx < quiz.questions.length - 1 && quiz.questions.length !== 1) {
          setNext(quiz.questions[idx + 1].id);
        } else {
          setNext(null);
        }
      }

      // Reset User Answer
      setUserAnswers([]);
    } catch (error) {
      if (error.response.status === 404) {
        // Delete Wrong History
        wrongHistory.delete(`${quiz.id}:${questionId}:${quizId}`);
        // Legacy
        wrongHistory.delete(`${quiz.id}:${questionId}`);

        setWrongHistory(wrongHistory);

        // Go to the next question
        setSearchParams({ questionId: next });

        // Set Next and Prev
        if (quiz) {
          const idx = quiz.questions.findIndex((q) => q.id === questionId);

          if (idx < quiz.questions.length - 1 && quiz.questions.length !== 1) {
            setSearchParams({ questionId: quiz.questions[idx + 1].id });
          } else {
            // Return to home
            navigate(`/practice/${quiz.id}`);
          }
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const renderQuestion = async () => {
    setIsFetching(true);
    if (questionId && quiz) {
      await getAnswers(questionId);

      if (answerHistory.has(questionId)) {
        // Render Previous Answer
        setUserAnswers(answerHistory.get(questionId));
        setIsSubmitted(true);
      } else {
        // Reset is submitted
        setIsSubmitted(false);
      }

      // Touch Question
      if (answerStatistics instanceof Map) {
        // Check if untouched
        if (answerStatistics.get(questionId) === "untouched") {
          answerStatistics.set(questionId, "touched");
          setAnswerStatistics(answerStatistics);
        }
      }

      setLoading(false);
    }
    setIsFetching(false);
  };
  const setSelectedAnswer = (answer) => {
    if (userAnswers.includes(answer)) {
      setUserAnswers(userAnswers.filter((a) => a.id !== answer.id));
    } else {
      setUserAnswers([...userAnswers, answer]);
    }
  };

  useEffect(() => {
    if (submitted === true) {
      let isCorrect = true;

      // Check if user has already answered the question
      if (!answerHistory.has(questionId)) {
        // Save Answer
        answerHistory.set(questionId, userAnswers);
        setAnswerHistory(answerHistory);

        // Check if userAnswer is correct or not
        for (const answer of question.answers) {
          // User answer a wrong answer

          if (userAnswers.includes(answer) && !answer.isCorrect) {
            isCorrect = false;
            // User didn't answer a correct answer
          } else if (!userAnswers.includes(answer) && answer.isCorrect) {
            isCorrect = false;
          }
        }

        if (!isCorrect) {
          // Save Wrong History
          wrongHistory.add(`${quiz.id}:${questionId}:${quizId}`);
          setWrongHistory(wrongHistory);

          // Question Statistics
          if (wrongStatistics.has(questionId)) {
            // Increment Wrong Count
            wrongStatistics.set(
              questionId,
              wrongStatistics.get(questionId) + 1
            );
          } else {
            // Set Wrong Count
            wrongStatistics.set(questionId, 1);
            console.log("New");
          }
          setWrongStatistics(wrongStatistics);
        } else {
          // Delete Wrong History
          wrongHistory.delete(`${quiz.id}:${questionId}:${quizId}`);
          // Legacy
          wrongHistory.delete(`${quiz.id}:${questionId}`);

          setWrongHistory(wrongHistory);
        }

        // Check if autoNext is enabled
        if (autoNext) {
          setStartedAutoNext(true);
          setTimeout(() => {
            if (next) {
              setSearchParams({ questionId: next });
              setStartedAutoNext(false);
            }
          }, 1000);
        }
      }

      // Update statistics and correctness
      answerStatistics.set(questionId, isCorrect ? "correct" : "wrong");
      setAnswerStatistics(answerStatistics);
      setIsSubmissionCorrect(isCorrect);
    }
  }, [submitted]);

  // Render Question
  useEffect(() => {
    if (isFetching) return;
    renderQuestion();
  }, [quiz, searchParams]);

  useEffect(() => {
    if (isFetching) return;
    renderQuestion();
  }, [quiz]);

  const getWrongQuestionIds = () => {
    let wrongQuestionIds = [];

    for (const raw of wrongHistory) {
      if (setId === raw.split(":")[0]) {
        wrongQuestionIds.push(raw.split(":")[1]);
      }
    }

    const practiceObj = practiceSetting.get(quizId);

    // Shuffle Questions
    if (practiceObj.isQuestionShuffled === true) {
      wrongQuestionIds.sort();
      wrongQuestionIds = shuffleWithSeed(wrongQuestionIds, setId);
    }

    return wrongQuestionIds;
  };

  // Get Quiz
  const init = async () => {
    setLoading(true);
    await getQuestionList();
  };

  useEffect(() => {
    init();
  }, []);

  if (isLoading) return <Loader />;
  if ((!quiz || !question) && !isLoading)
    return <PageNotFound backPath={`/practice/${quizId}`} />;
  return (
    <>
      <PracticeHeader
        quiz={quiz}
        quizId={quizId}
        question={question}
        setQuestionListModalOpen={setQuestionListModalOpen}
        mode={"redo"}
        autoNext={autoNext}
        setAutoNext={setAutoNext}
      />

      <main>
        <RedoQuestionListModal
          importModalOpen={questionListModalOpen}
          setImportModalOpen={setQuestionListModalOpen}
          quizId={quizId}
          setId={setId}
        />

        <QuestionMain
          quiz={quiz}
          question={question}
          userAnswers={userAnswers}
          submitted={submitted}
          setIsSubmitted={setIsSubmitted}
          setSelectedAnswer={setSelectedAnswer}
          setQuestionListModalOpen={setQuestionListModalOpen}
          isSubmittionCorect={isSubmittionCorect}
          wrongCount={wrongStatistics.get(questionId)}
        />

        <RedoQuestionAnswer
          quiz={quiz}
          question={question}
          quizId={quizId}
          setId={setId}
          submitted={submitted}
          setIsSubmitted={setIsSubmitted}
          next={next}
          prev={prev}
          setSearchParams={setSearchParams}
          isFetching={isFetching}
          startedAutoNext={startedAutoNext}
        />
      </main>
    </>
  );
}
